import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Mail, MessageCircle, Smartphone, Bell, Send, MessageSquare,
  X, Search, Plus, Users, Filter, Paperclip, Smile, Zap, Pin,
  Check, CheckCheck, Clock, Eye, Copy, FileText, Image as ImageIcon,
  Sparkles, ChevronRight, AlertCircle, CheckCircle2, Save, Trash2,
  AtSign, Hash, Calendar, ArrowRight, Layers, Monitor, Smartphone as PhoneIcon,
  Volume2, VolumeX, Star, Bookmark, RefreshCw, TrendingUp, User,
  GraduationCap, Building2, Store, Shield, Briefcase, Activity,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { toast } from "sonner";

const FOREST = "#0b4f45";
const FOREST_DEEP = "#073a34";
const FOREST_TINT = "#E8F1EA";
const FOREST_MID = "#1E7A45";
const ACCENT_GOLD = "#C28B3A";
const ACCENT_GOLD_LIGHT = "#F4C251";
const TERRACOTTA = "#CC746A";
const INK = "#101511";
const MUTED = "#667069";
const BORDER = "#E2E7E3";
const BG_SUBTLE = "#F4F7F5";
const BG_CHAT = "#FAFBFA";

export interface ChannelDef {
  key: string;
  label: string;
  icon: any;
  color: string;
  desc: string;
  segment: string;
  maxLength?: number;
  costPerMessage?: string;
}

export const CHANNELS: ChannelDef[] = [
  { key: "email", label: "Email", icon: Mail, color: "#2F7E67", desc: "Rich HTML email", segment: "Email", costPerMessage: "Free" },
  { key: "whatsapp", label: "WhatsApp", icon: MessageCircle, color: "#25D366", desc: "WhatsApp Business", segment: "WhatsApp", costPerMessage: "~$0.005/msg" },
  { key: "sms", label: "SMS", icon: Smartphone, color: "#C28B3A", desc: "SMS gateway", segment: "SMS", maxLength: 160, costPerMessage: "~$0.02/msg" },
  { key: "inapp", label: "In-App", icon: Bell, color: "#0ea5e9", desc: "Push notification", segment: "In-App", costPerMessage: "Free" },
  { key: "push", label: "Push", icon: Send, color: "#CC746A", desc: "Mobile push", segment: "Push", maxLength: 100, costPerMessage: "Free" },
  { key: "internal", label: "Internal", icon: MessageSquare, color: "#0b4f45", desc: "Internal message", segment: "Internal", costPerMessage: "Free" },
];

const AUDIENCE_PRESETS = [
  { key: "all_parents", label: "All parents", icon: Users, count: 8432, breakdown: [{ label: "Parents", count: 8432 }] },
  { key: "all_students", label: "All students", icon: GraduationCap, count: 12480, breakdown: [{ label: "Students", count: 12480 }] },
  { key: "all_merchants", label: "All merchants", icon: Store, count: 128, breakdown: [{ label: "Merchants", count: 128 }] },
  { key: "school_admins", label: "School admins", icon: Building2, count: 42, breakdown: [{ label: "School Admins", count: 42 }] },
  { key: "active_users", label: "Active (30d)", icon: Activity, count: 9821, breakdown: [{ label: "Parents", count: 6200 }, { label: "Students", count: 3100 }, { label: "Staff", count: 521 }] },
  { key: "inactive", label: "Inactive (60d+)", icon: AlertCircle, count: 2340, breakdown: [{ label: "Parents", count: 1840 }, { label: "Students", count: 500 }] },
];

const TEMPLATE_VARS = [
  "{{first_name}}", "{{last_name}}", "{{school_name}}", "{{merchant_name}}",
  "{{amount}}", "{{due_date}}", "{{transaction_id}}", "{{account_balance}}",
];

const TEMPLATES = [
  { id: "t1", name: "Fee reminder", category: "Payment", body: "Dear {{first_name}}, school fees of {{amount}} are due on {{due_date}} for {{school_name}}." },
  { id: "t2", name: "Payment confirmation", category: "Transaction", body: "Payment of {{amount}} received by {{merchant_name}}. Thank you, {{first_name}}." },
  { id: "t3", name: "Term opening", category: "School", body: "Hello {{first_name}}! {{school_name}} Term 2 opens Monday. See you there!" },
  { id: "t4", name: "Safety alert", category: "Emergency", body: "Safety alert for {{school_name}}. Tap for details." },
  { id: "t5", name: "Welcome", category: "Welcome", body: "Welcome to {{school_name}}, {{first_name}}! Your account is ready." },
  { id: "t6", name: "Wallet top-up", category: "Marketing", body: "{{first_name}}, your balance is {{account_balance}}. Top up now to keep spending!" },
];

const EMOJIS = ["👍", "❤️", "🎉", "✅", "👀", "🔥", "👏", "🙏", "💡", "⚡", "📅", "💰"];

function getInitials(name: string): string {
  if (!name) return "?";
  return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
}

function fmtNum(v: number): string {
  return new Intl.NumberFormat("en-US").format(v);
}

export interface OmnichannelComposerProps {
  open: boolean;
  onClose: () => void;
  onSend?: (data: { channels: string[]; subject: string; body: string; audience: string; priority: string; schedule: string }) => void;
  mode?: "internal" | "omnichannel";
  defaultChannels?: string[];
}

export function OmnichannelComposer({
  open,
  onClose,
  onSend,
  mode = "omnichannel",
  defaultChannels = ["email", "inapp"],
}: OmnichannelComposerProps) {
  const [step, setStep] = useState(0);
  const [channels, setChannels] = useState<string[]>(defaultChannels);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [audience, setAudience] = useState("all_parents");
  const [priority, setPriority] = useState("normal");
  const [schedule, setSchedule] = useState("now");
  const [search, setSearch] = useState("");
  const [selectedRecipients, setSelectedRecipients] = useState<Array<{ id: string; name: string; role: string }>>([]);
  const [showEmoji, setShowEmoji] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewChannel, setPreviewChannel] = useState(0);
  const [previewDevice, setPreviewDevice] = useState<"desktop" | "mobile">("desktop");
  const [attachments, setAttachments] = useState<Array<{ name: string; size: string; type: string }>>([]);
  const [draftSaved, setDraftSaved] = useState(false);
  const [activeChannelTab, setActiveChannelTab] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const selectedAudience = AUDIENCE_PRESETS.find(a => a.key === audience);
  const activeChannels = CHANNELS.filter(c => channels.includes(c.key));

  const toggleChannel = useCallback((k: string) => {
    setChannels(prev => prev.includes(k) ? prev.filter(c => c !== k) : [...prev, k]);
  }, []);

  const insertVar = (v: string) => {
    setBody(prev => prev + " " + v);
    toast.success(`Inserted ${v}`, { duration: 1500 });
  };

  const insertEmoji = (e: string) => {
    setBody(prev => prev + e);
    setShowEmoji(false);
  };

  const applyTemplate = (t: typeof TEMPLATES[0]) => {
    setBody(t.body);
    setShowTemplates(false);
    toast.success(`Template "${t.name}" applied`, { duration: 1500 });
  };

  const handleAttach = () => fileInputRef.current?.click();

  const onFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    Array.from(files).forEach(f => {
      setAttachments(prev => [...prev, {
        name: f.name,
        size: f.size > 1048576 ? `${(f.size / 1048576).toFixed(1)} MB` : `${Math.round(f.size / 1024)} KB`,
        type: f.type.includes("image") ? "image" : f.type.includes("pdf") ? "pdf" : "file",
      }]);
    });
    toast.success("File attached", { duration: 1500 });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeAttachment = (idx: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== idx));
  };

  const saveDraft = () => {
    setDraftSaved(true);
    toast.success("Draft saved", { duration: 1500 });
    setTimeout(() => setDraftSaved(false), 2000);
  };

  const handleSend = () => {
    if (!body.trim()) { toast.error("Message body is empty"); return; }
    if (channels.length === 0) { toast.error("Select at least one channel"); return; }
    onSend?.({ channels, subject, body, audience, priority, schedule });
    toast.success(`Message queued for ${selectedAudience?.label ?? "audience"} via ${channels.length} channel${channels.length > 1 ? "s" : ""}`);
    onClose();
  };

  // Validation
  const validations = useMemo(() => {
    const checks: { type: "warn" | "ok" | "error"; message: string }[] = [];
    if (channels.length === 0) checks.push({ type: "error", message: "No channel selected" });
    if (!body.trim()) checks.push({ type: "error", message: "Message body is empty" });
    if (channels.includes("sms") && body.length > 160) checks.push({ type: "warn", message: `SMS exceeds one segment (${body.length}/160 chars)` });
    if (channels.includes("whatsapp") && !body.includes("{{")) checks.push({ type: "ok", message: "WhatsApp content ready" });
    if (channels.includes("email") && !subject.trim()) checks.push({ type: "warn", message: "Email has no subject" });
    if (body.trim() && channels.length > 0) checks.push({ type: "ok", message: `${fmtNum(selectedAudience?.count ?? 0)} recipients reachable` });
    return checks;
  }, [channels, body, subject, selectedAudience]);

  const smsSegments = Math.ceil(body.length / 160);
  const estimatedCost = useMemo(() => {
    let cost = 0;
    if (channels.includes("sms")) cost += (selectedAudience?.count ?? 0) * 0.02;
    if (channels.includes("whatsapp")) cost += (selectedAudience?.count ?? 0) * 0.005;
    return cost > 0 ? `$${cost.toFixed(2)}` : "Free";
  }, [channels, selectedAudience]);

  // Reset state when opened
  useEffect(() => {
    if (open) {
      setChannels(defaultChannels);
      setSubject(""); setBody(""); setAudience("all_parents");
      setPriority("normal"); setSchedule("now"); setAttachments([]);
      setSelectedRecipients([]); setShowPreview(false); setShowTemplates(false);
      setActiveChannelTab(0); setPreviewChannel(0);
    }
  }, [open]);

  if (!open) return null;

  return createPortal(
    <AnimatePresence>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[80] bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 10 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        className="fixed left-1/2 top-1/2 z-[81] flex max-h-[85vh] w-[94vw] max-w-[860px] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-3xl bg-white shadow-2xl"
      >
        {/* Hidden file input */}
        <input ref={fileInputRef} type="file" multiple className="hidden" onChange={onFileSelected} />

        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4" style={{ borderColor: BORDER, background: `linear-gradient(135deg, ${FOREST} 0%, ${FOREST_DEEP} 100%)` }}>
          <div className="flex items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-xl" style={{ background: "rgba(255,255,255,0.1)" }}>
              <Sparkles className="h-5 w-5" style={{ color: ACCENT_GOLD_LIGHT }} />
            </div>
            <div>
              <h2 className="font-display text-lg font-bold text-white">New Message</h2>
              <p className="text-xs text-white/60">Reach your audience across the channels that matter.</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {draftSaved && (
              <motion.span initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }} className="flex items-center gap-1 text-xs text-white/60">
                <CheckCircle2 className="h-3.5 w-3.5" /> Draft saved
              </motion.span>
            )}
            <button onClick={saveDraft} className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium text-white/80 transition hover:bg-white/10" title="Save draft">
              <Save className="h-3.5 w-3.5" /> Save Draft
            </button>
            <button onClick={() => setShowPreview(!showPreview)} className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition ${showPreview ? "bg-white/15 text-white" : "text-white/80 hover:bg-white/10"}`} title="Preview">
              <Eye className="h-3.5 w-3.5" /> Preview
            </button>
            <button onClick={onClose} className="grid h-8 w-8 place-items-center rounded-lg text-white/70 transition hover:bg-white/10" title="Close">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Body — scrollable */}
        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px]">
            {/* Left — Composer */}
            <div className="p-6 space-y-5">
              {/* Channel Bar */}
              <div>
                <div className="flex items-center justify-between mb-2.5">
                  <label className="text-xs font-bold uppercase tracking-wider" style={{ color: MUTED }}>Channels</label>
                  <span className="text-[10px]" style={{ color: MUTED }}>{channels.length} selected · Select one or more</span>
                </div>
                <div className="grid grid-cols-3 gap-2.5 sm:grid-cols-6">
                  {CHANNELS.map(c => {
                    const active = channels.includes(c.key);
                    return (
                      <motion.button
                        key={c.key}
                        whileHover={{ y: -2 }}
                        whileTap={{ scale: 0.97 }}
                        onClick={() => toggleChannel(c.key)}
                        className={`relative flex flex-col items-center gap-1.5 rounded-2xl border p-3 text-center transition`}
                        style={{
                          borderColor: active ? c.color : BORDER,
                          background: active ? `${c.color}08` : "white",
                          boxShadow: active ? `0 4px 14px ${c.color}20` : "none",
                        }}
                      >
                        {active && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="absolute -right-1.5 -top-1.5 grid h-5 w-5 place-items-center rounded-full text-white shadow-sm"
                            style={{ background: c.color }}
                          >
                            <Check className="h-3 w-3" />
                          </motion.div>
                        )}
                        <div className="grid h-10 w-10 place-items-center rounded-xl transition" style={{ background: active ? `${c.color}15` : BG_SUBTLE }}>
                          <c.icon className="h-5 w-5 transition" style={{ color: active ? c.color : MUTED }} />
                        </div>
                        <span className="text-xs font-semibold" style={{ color: active ? INK : MUTED }}>{c.label}</span>
                        <span className="text-[9px] leading-tight" style={{ color: MUTED }}>{c.desc}</span>
                      </motion.button>
                    );
                  })}
                </div>
              </div>

              {/* Multi-channel tabs */}
              {activeChannels.length > 1 && (
                <div className="flex items-center gap-1 rounded-xl border p-1" style={{ borderColor: BORDER, background: BG_SUBTLE }}>
                  {activeChannels.map((c, i) => (
                    <button
                      key={c.key}
                      onClick={() => setActiveChannelTab(i)}
                      className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition ${activeChannelTab === i ? "bg-white shadow-sm" : ""}`}
                      style={{ color: activeChannelTab === i ? c.color : MUTED }}
                    >
                      <c.icon className="h-3.5 w-3.5" /> {c.label}
                    </button>
                  ))}
                  <span className="ml-auto text-[10px] px-2" style={{ color: MUTED }}>Editing per-channel content</span>
                </div>
              )}

              {/* Recipient / Audience */}
              <div>
                <label className="text-xs font-bold uppercase tracking-wider mb-2 block" style={{ color: MUTED }}>To</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" style={{ color: MUTED }} />
                  <Input
                    placeholder="Search people, schools, merchants or groups..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>
                {selectedRecipients.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {selectedRecipients.map(r => (
                      <motion.span
                        key={r.id}
                        initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                        className="flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs" style={{ background: FOREST_TINT, color: FOREST }}
                      >
                        <Avatar className="h-4 w-4"><AvatarFallback style={{ background: "white", color: FOREST, fontSize: "0.5rem" }}>{getInitials(r.name)}</AvatarFallback></Avatar>
                        {r.name}
                        <button onClick={() => setSelectedRecipients(prev => prev.filter(x => x.id !== r.id))}><X className="h-3 w-3" /></button>
                      </motion.span>
                    ))}
                  </div>
                )}
              </div>

              {/* Subject (email/inapp/push) */}
              {(channels.includes("email") || channels.includes("inapp") || channels.includes("push")) && (
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider mb-1.5 block" style={{ color: MUTED }}>
                    {channels.includes("email") ? "Subject" : "Title"}
                  </label>
                  <Input
                    value={subject}
                    onChange={e => setSubject(e.target.value)}
                    placeholder={channels.includes("email") ? "Term 2 fees due Friday..." : "Notification title..."}
                  />
                </div>
              )}

              {/* Message body */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-bold uppercase tracking-wider" style={{ color: MUTED }}>Message</label>
                  <div className="flex items-center gap-1">
                    <button onClick={() => setShowEmoji(!showEmoji)} className="grid h-7 w-7 place-items-center rounded-lg hover:bg-slate-100" title="Emoji">
                      <Smile className="h-4 w-4" style={{ color: MUTED }} />
                    </button>
                    <button onClick={() => setShowTemplates(!showTemplates)} className="grid h-7 w-7 place-items-center rounded-lg hover:bg-slate-100" title="Templates">
                      <Copy className="h-4 w-4" style={{ color: MUTED }} />
                    </button>
                    <button onClick={handleAttach} className="grid h-7 w-7 place-items-center rounded-lg hover:bg-slate-100" title="Attach">
                      <Paperclip className="h-4 w-4" style={{ color: MUTED }} />
                    </button>
                  </div>
                </div>

                {/* Emoji picker */}
                {showEmoji && (
                  <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} className="mb-2 grid grid-cols-6 gap-1 rounded-xl border p-2" style={{ borderColor: BORDER }}>
                    {EMOJIS.map(e => (
                      <button key={e} onClick={() => insertEmoji(e)} className="grid h-8 w-8 place-items-center rounded-lg text-lg hover:bg-slate-100">{e}</button>
                    ))}
                  </motion.div>
                )}

                {/* Template picker */}
                {showTemplates && (
                  <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} className="mb-2 space-y-1.5 rounded-xl border p-2" style={{ borderColor: BORDER }}>
                    <div className="text-[10px] font-bold uppercase tracking-wider px-1" style={{ color: MUTED }}>Templates</div>
                    {TEMPLATES.map(t => (
                      <button key={t.id} onClick={() => applyTemplate(t)} className="flex w-full items-center gap-2.5 rounded-lg p-2 text-left hover:bg-slate-50">
                        <FileText className="h-4 w-4 shrink-0" style={{ color: FOREST_MID }} />
                        <div className="min-w-0 flex-1">
                          <div className="text-xs font-medium" style={{ color: INK }}>{t.name}</div>
                          <div className="truncate text-[10px]" style={{ color: MUTED }}>{t.body}</div>
                        </div>
                        <Badge variant="outline" className="text-[9px] shrink-0">{t.category}</Badge>
                      </button>
                    ))}
                  </motion.div>
                )}

                <textarea
                  value={body}
                  onChange={e => setBody(e.target.value)}
                  rows={6}
                  placeholder="Dear {{first_name}}, your {{school_name}} fees of {{amount}} are due on {{due_date}}..."
                  className="w-full resize-none rounded-xl border px-3.5 py-3 text-sm outline-none transition focus:ring-2"
                  style={{ borderColor: BORDER }}
                />

                {/* Personalisation vars */}
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {TEMPLATE_VARS.map(v => (
                    <button key={v} onClick={() => insertVar(v)} className="flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] transition hover:bg-slate-50" style={{ borderColor: BORDER, color: MUTED }}>
                      <Sparkles className="h-2.5 w-2.5" /> {v}
                    </button>
                  ))}
                </div>

                {/* SMS counter */}
                {channels.includes("sms") && (
                  <div className="mt-2 flex items-center gap-2 text-[10px]" style={{ color: MUTED }}>
                    <span style={{ color: body.length > 160 ? TERRACOTTA : MUTED }}>{body.length}/160</span>
                    <span>·</span>
                    <span>{smsSegments} segment{smsSegments > 1 ? "s" : ""}</span>
                    <span>·</span>
                    <span>{body.match(/[^\x00-\x7F]/) ? "Unicode" : "GSM-7"}</span>
                  </div>
                )}
              </div>

              {/* Attachments */}
              {attachments.length > 0 && (
                <div className="space-y-2">
                  {attachments.map((a, i) => (
                    <motion.div key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} className="flex items-center gap-3 rounded-xl border p-2.5" style={{ borderColor: BORDER, background: BG_SUBTLE }}>
                      <div className="grid h-9 w-9 place-items-center rounded-lg" style={{ background: a.type === "image" ? "#0ea5e915" : a.type === "pdf" ? "#CC746A15" : "#C28B3A15" }}>
                        {a.type === "image" ? <ImageIcon className="h-4 w-4" style={{ color: "#0ea5e9" }} /> : <FileText className="h-4 w-4" style={{ color: TERRACOTTA }} />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-xs font-medium" style={{ color: INK }}>{a.name}</div>
                        <div className="text-[10px]" style={{ color: MUTED }}>{a.size} · {a.type.toUpperCase()}</div>
                      </div>
                      <button onClick={() => removeAttachment(i)} className="grid h-7 w-7 place-items-center rounded-lg hover:bg-slate-200"><Trash2 className="h-3.5 w-3.5" style={{ color: TERRACOTTA }} /></button>
                    </motion.div>
                  ))}
                </div>
              )}

              {/* Priority */}
              <div>
                <label className="text-xs font-bold uppercase tracking-wider mb-1.5 block" style={{ color: MUTED }}>Priority</label>
                <div className="flex gap-2">
                  {([
                    { value: "normal", label: "Normal", icon: Hash, color: FOREST_MID },
                    { value: "high", label: "High", icon: Pin, color: ACCENT_GOLD },
                    { value: "urgent", label: "Urgent", icon: Zap, color: TERRACOTTA },
                  ] as const).map(opt => {
                    const active = priority === opt.value;
                    return (
                      <button key={opt.value} onClick={() => setPriority(opt.value)}
                        className={`flex items-center gap-1.5 rounded-xl px-3.5 py-2 text-xs font-medium transition border ${active ? "border-2" : "border"}`}
                        style={{ background: active ? `${opt.color}10` : "white", borderColor: active ? opt.color : BORDER, color: active ? opt.color : MUTED }}>
                        <opt.icon className="h-3.5 w-3.5" /> {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Validation */}
              {validations.length > 0 && (
                <div className="space-y-1.5 rounded-xl border p-3" style={{ borderColor: BORDER, background: BG_SUBTLE }}>
                  {validations.map((v, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      {v.type === "error" ? <AlertCircle className="h-3.5 w-3.5" style={{ color: TERRACOTTA }} /> :
                       v.type === "warn" ? <AlertCircle className="h-3.5 w-3.5" style={{ color: ACCENT_GOLD }} /> :
                       <CheckCircle2 className="h-3.5 w-3.5" style={{ color: FOREST_MID }} />}
                      <span style={{ color: v.type === "ok" ? FOREST_MID : v.type === "warn" ? ACCENT_GOLD : TERRACOTTA }}>{v.message}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Preview panel */}
              {showPreview && activeChannels.length > 0 && (
                <div className="rounded-2xl border p-4" style={{ borderColor: BORDER, background: BG_CHAT }}>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-bold uppercase tracking-wider" style={{ color: MUTED }}>Preview</span>
                    <div className="flex items-center gap-1">
                      <button onClick={() => setPreviewDevice("desktop")} className={`grid h-7 w-7 place-items-center rounded-lg transition ${previewDevice === "desktop" ? "bg-white shadow-sm" : ""}`}>
                        <Monitor className="h-3.5 w-3.5" style={{ color: previewDevice === "desktop" ? FOREST : MUTED }} />
                      </button>
                      <button onClick={() => setPreviewDevice("mobile")} className={`grid h-7 w-7 place-items-center rounded-lg transition ${previewDevice === "mobile" ? "bg-white shadow-sm" : ""}`}>
                        <PhoneIcon className="h-3.5 w-3.5" style={{ color: previewDevice === "mobile" ? FOREST : MUTED }} />
                      </button>
                    </div>
                  </div>
                  <div className="flex gap-1 mb-3">
                    {activeChannels.map((c, i) => (
                      <button key={c.key} onClick={() => setPreviewChannel(i)}
                        className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-medium transition ${previewChannel === i ? "text-white" : ""}`}
                        style={{ background: previewChannel === i ? c.color : BG_SUBTLE, color: previewChannel === i ? "white" : MUTED }}>
                        <c.icon className="h-3 w-3" /> {c.label}
                      </button>
                    ))}
                  </div>
                  <ChannelPreview channel={activeChannels[previewChannel] || activeChannels[0]} subject={subject} body={body} device={previewDevice} />
                </div>
              )}
            </div>

            {/* Right — Audience sidebar */}
            <div className="border-l p-5 space-y-4" style={{ borderColor: BORDER, background: BG_SUBTLE }}>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider mb-2 block" style={{ color: MUTED }}>Audience</label>
                <div className="space-y-1.5">
                  {AUDIENCE_PRESETS.map(a => {
                    const active = audience === a.key;
                    return (
                      <button key={a.key} onClick={() => setAudience(a.key)}
                        className={`flex w-full items-center gap-3 rounded-xl border p-2.5 text-left transition ${active ? "" : "hover:bg-white"}`}
                        style={{ borderColor: active ? FOREST : BORDER, background: active ? "white" : "transparent" }}>
                        <div className="grid h-8 w-8 place-items-center rounded-lg" style={{ background: active ? FOREST_TINT : "white" }}>
                          <a.icon className="h-4 w-4" style={{ color: active ? FOREST : MUTED }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-medium" style={{ color: INK }}>{a.label}</div>
                          <div className="text-[10px]" style={{ color: MUTED }}>{fmtNum(a.count)} recipients</div>
                        </div>
                        {active && <Check className="h-4 w-4" style={{ color: FOREST }} />}
                      </button>
                    );
                  })}
                </div>
                <button className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-xl border border-dashed p-2.5 text-xs transition hover:bg-white" style={{ borderColor: BORDER, color: MUTED }}>
                  <Filter className="h-3.5 w-3.5" /> Build custom audience
                </button>
              </div>

              {/* Audience breakdown */}
              {selectedAudience && (
                <div className="rounded-xl border p-3" style={{ borderColor: BORDER, background: "white" }}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: MUTED }}>Estimated reach</span>
                    <button className="text-[10px] font-medium hover:underline" style={{ color: FOREST }}>View</button>
                  </div>
                  <div className="font-display text-2xl font-bold" style={{ color: INK }}>{fmtNum(selectedAudience.count)}</div>
                  <div className="text-[10px] mb-2" style={{ color: MUTED }}>recipients across {channels.length || "—"} channel{channels.length > 1 ? "s" : ""}</div>
                  <div className="space-y-1">
                    {selectedAudience.breakdown.map(b => (
                      <div key={b.label} className="flex items-center justify-between text-[10px]">
                        <span style={{ color: MUTED }}>{b.label}</span>
                        <span className="font-semibold" style={{ color: INK }}>{fmtNum(b.count)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Cost estimate */}
              <div className="rounded-xl border p-3" style={{ borderColor: BORDER, background: "white" }}>
                <div className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: MUTED }}>Estimated cost</div>
                <div className="font-display text-lg font-bold" style={{ color: estimatedCost === "Free" ? FOREST_MID : INK }}>{estimatedCost}</div>
                <div className="text-[10px]" style={{ color: MUTED }}>{channels.length} channel{channels.length > 1 ? "s" : ""} · {fmtNum(selectedAudience?.count ?? 0)} recipients</div>
              </div>

              {/* Schedule */}
              <div>
                <label className="text-xs font-bold uppercase tracking-wider mb-1.5 block" style={{ color: MUTED }}>Schedule</label>
                <div className="grid grid-cols-2 gap-1.5">
                  {([
                    { value: "now", label: "Send now", icon: Zap },
                    { value: "1h", label: "In 1 hour", icon: Clock },
                    { value: "tomorrow", label: "Tomorrow 8AM", icon: Calendar },
                    { value: "custom", label: "Custom...", icon: Calendar },
                  ] as const).map(s => (
                    <button key={s.value} onClick={() => setSchedule(s.value)}
                      className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-2 text-[11px] font-medium transition ${schedule === s.value ? "" : "hover:bg-white"}`}
                      style={{ borderColor: schedule === s.value ? FOREST : BORDER, background: schedule === s.value ? FOREST_TINT : "transparent", color: schedule === s.value ? FOREST : MUTED }}>
                      <s.icon className="h-3 w-3" /> {s.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer — Send controls */}
        <div className="flex items-center justify-between border-t px-6 py-3.5" style={{ borderColor: BORDER, background: BG_SUBTLE }}>
          <div className="flex items-center gap-2">
            <button onClick={handleAttach} className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium transition hover:bg-white" style={{ color: MUTED }}>
              <Paperclip className="h-3.5 w-3.5" /> Attach
            </button>
            <button onClick={() => toast.info("Test message sent")} className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium transition hover:bg-white" style={{ color: MUTED }}>
              <Send className="h-3.5 w-3.5" /> Test send
            </button>
            <div className="flex items-center gap-1.5 text-[11px]" style={{ color: MUTED }}>
              <span>{fmtNum(selectedAudience?.count ?? 0)} recipients</span>
              <span>·</span>
              <span>{channels.length} channel{channels.length > 1 ? "s" : ""}</span>
              {estimatedCost !== "Free" && <><span>·</span><span style={{ color: ACCENT_GOLD }}>{estimatedCost}</span></>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={saveDraft} className="border-slate-200">
              <Save className="mr-1.5 h-3.5 w-3.5" /> Save Draft
            </Button>
            <Button size="sm" onClick={handleSend} className="text-white" style={{ background: FOREST, border: "none" }}>
              <Zap className="mr-1.5 h-3.5 w-3.5" /> {schedule === "now" ? "Send Now" : "Schedule"}
            </Button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>,
    document.body,
  );
}

function ChannelPreview({ channel, subject, body, device }: { channel: ChannelDef; subject: string; body: string; device: "desktop" | "mobile" }) {
  if (!channel) return null;
  const Icon = channel.icon;
  const isMobile = device === "mobile";

  return (
    <div className={`mx-auto ${isMobile ? "max-w-[280px]" : "max-w-full"}`}>
      <div className="rounded-2xl border bg-white p-4 shadow-sm" style={{ borderColor: BORDER }}>
        <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: channel.color }}>
          <Icon className="h-3.5 w-3.5" /> {channel.label} Preview
        </div>
        {channel.key === "email" && (
          <div>
            <div className="text-sm font-bold mb-1" style={{ color: INK }}>{subject || "Your subject line"}</div>
            <div className="text-xs leading-relaxed" style={{ color: MUTED }}>{body || "Your message body will appear here."}</div>
          </div>
        )}
        {channel.key === "whatsapp" && (
          <div className="rounded-xl p-3" style={{ background: "#DCF8C6" }}>
            <div className="text-xs leading-relaxed" style={{ color: INK }}>{body || "Your WhatsApp message"}</div>
            <div className="mt-1 text-[9px] text-right" style={{ color: MUTED }}>12:34 PM ✓✓</div>
          </div>
        )}
        {channel.key === "sms" && (
          <div className="rounded-xl p-3" style={{ background: BG_SUBTLE }}>
            <div className="text-xs leading-relaxed" style={{ color: INK }}>{body || "Your SMS message"}</div>
            <div className="mt-1 text-[9px]" style={{ color: MUTED }}>From: BRECCS</div>
          </div>
        )}
        {channel.key === "inapp" && (
          <div className="rounded-xl border p-3" style={{ borderColor: BORDER }}>
            <div className="flex items-center gap-2 mb-1">
              <div className="grid h-6 w-6 place-items-center rounded-lg" style={{ background: channel.color }}><Bell className="h-3 w-3 text-white" /></div>
              <span className="text-xs font-bold" style={{ color: INK }}>{subject || "Notification title"}</span>
            </div>
            <div className="text-xs" style={{ color: MUTED }}>{body || "Your in-app notification"}</div>
          </div>
        )}
        {channel.key === "push" && (
          <div className={`rounded-xl border p-2.5 ${isMobile ? "" : "max-w-xs"}`} style={{ borderColor: BORDER, background: BG_SUBTLE }}>
            <div className="flex items-center gap-2">
              <div className="grid h-7 w-7 place-items-center rounded-lg" style={{ background: channel.color }}><Send className="h-3.5 w-3.5 text-white" /></div>
              <div className="min-w-0 flex-1">
                <div className="text-[11px] font-bold" style={{ color: INK }}>{subject || "Push title"}</div>
                <div className="truncate text-[10px]" style={{ color: MUTED }}>{body || "Push notification body"}</div>
              </div>
              <span className="text-[9px]" style={{ color: MUTED }}>now</span>
            </div>
          </div>
        )}
        {channel.key === "internal" && (
          <div className="rounded-xl p-3" style={{ background: FOREST_TINT }}>
            <div className="flex items-center gap-2 mb-1">
              <div className="grid h-6 w-6 place-items-center rounded-lg" style={{ background: FOREST }}><MessageSquare className="h-3 w-3 text-white" /></div>
              <span className="text-xs font-bold" style={{ color: FOREST }}>Internal Message</span>
            </div>
            <div className="text-xs leading-relaxed" style={{ color: INK }}>{body || "Your internal message"}</div>
          </div>
        )}
      </div>
    </div>
  );
}
